import { Velocity } from "@/lib/audio/synth";
import { buildLayout } from "@/lib/virtuosa-layout/layout";
import type { VirtuosaLayout } from "@/lib/virtuosa-layout/types";
import { Note } from "../../music/note";
import { Piano } from "../piano";
import { PianoInput } from "./input";

export class ComputerKeyboardInput implements PianoInput {
  private down = new Set<string>();
  private codeToMidi = new Map<string, number>();
  private readonly layout: VirtuosaLayout;
  private readonly velocity: Velocity;
  private readonly target: Window | HTMLElement;

  constructor(
    private readonly piano: Piano,
    opts?: {
      layout?: VirtuosaLayout;
      velocity?: number;
      target?: Window | HTMLElement;
    },
  ) {
    this.layout = opts?.layout ?? buildLayout();
    this.velocity = opts?.velocity ?? 0.5;
    this.target = opts?.target ?? window;
  }

  attach(): void {
    this.target.addEventListener("keydown", this.onKeyDown as EventListener);
    this.target.addEventListener("keyup", this.onKeyUp as EventListener);
    window.addEventListener("blur", this.onBlur);
  }

  detach(): void {
    this.target.removeEventListener("keydown", this.onKeyDown as EventListener);
    this.target.removeEventListener("keyup", this.onKeyUp as EventListener);
    window.removeEventListener("blur", this.onBlur);
    this.releaseAll();
  }

  getLayout(): VirtuosaLayout {
    return this.layout;
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (this.isTypingTarget(e)) {
      return;
    }

    if (e.repeat) {
      return;
    }

    if (e.altKey || e.ctrlKey || e.metaKey) {
      return;
    }

    if (e.code === "Space") {
      e.preventDefault();

      if (this.down.has(e.code)) {
        return;
      }

      this.down.add(e.code);
      this.piano.sustainOn();

      return;
    }

    const key = this.layout.byCode.get(e.code);

    if (!key) {
      return;
    }

    e.preventDefault();

    if (this.down.has(e.code)) {
      return;
    }

    this.down.add(e.code);

    const note = Note.fromMidi(key.midi);

    if (!this.piano.contains(note)) {
      return;
    }

    this.codeToMidi.set(e.code, key.midi);
    this.piano.press(note, this.velocity);
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    if (this.isTypingTarget(e)) {
      return;
    }

    if (e.code === "Space") {
      e.preventDefault();
      this.down.delete(e.code);
      this.piano.sustainOff();

      return;
    }

    const midi = this.codeToMidi.get(e.code);

    if (midi == null) {
      return;
    }

    e.preventDefault();

    this.down.delete(e.code);
    this.codeToMidi.delete(e.code);
    this.piano.release(Note.fromMidi(midi));
  };

  private readonly onBlur = (): void => {
    this.releaseAll();
  };

  private isTypingTarget(e: KeyboardEvent): boolean {
    const target = e.target as HTMLElement | null;

    if (!target) {
      return false;
    }

    const tag = target.tagName;

    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
      return true;
    }

    return target.isContentEditable;
  }

  private releaseAll() {
    for (const midi of this.codeToMidi.values()) {
      this.piano.release(Note.fromMidi(midi));
    }

    this.down.clear();
    this.codeToMidi.clear();
    this.piano.allNotesOff();
  }
}
